/**
 * guardian-hero.js
 * Inicializa el visor 3D de la sección "guardian-hero.liquid".
 * Importa three.js desde CDN vía importmap (declarado en el <head> del
 * theme.liquid — ver README) y las funciones compartidas de
 * guardian-viewer-core.js (mismo directorio /assets; el import relativo
 * se resuelve contra la URL de este propio módulo, así que funciona sin
 * paso de build en el CDN de Shopify).
 *
 * Import DINÁMICO con versión manual (en vez de un `import ... from`
 * estático): este propio archivo (guardian-hero.js) se sirve a través
 * de `asset_url` de Liquid, que le añade un `?v=...` que Shopify
 * cambia solo cada vez que lo editas — por eso SÍ se refresca al
 * actualizarlo. Pero un `import` estático de otro archivo .js usa una
 * ruta fija sin ese parámetro, así que cualquier caché intermedia
 * (CDN/proxy delante del dominio, no la del navegador) puede quedarse
 * sirviendo una copia vieja de guardian-viewer-core.js indefinidamente,
 * por mucho que lo edites y guardes en Shopify. Al meterle aquí un
 * número de versión a mano, cada vez que ese archivo cambie de verdad
 * basta con subir este número para forzar una URL nueva y evitar
 * cualquier caché pegada a la versión antigua.
 * Súbelo cada vez que publiques un cambio en guardian-viewer-core.js.
 */
const GUARDIAN_CORE_VERSION = '6';

async function initGuardianHero(root) {
  const wrap = root.querySelector('[data-guardian-viewer]');
  const statusEl = root.querySelector('[data-guardian-status]');
  if (!wrap) return;

  function setStatus(text, kind) {
    if (!statusEl) return;
    statusEl.textContent = text;
    statusEl.className = 'guardian-status' + (kind ? ' guardian-status--' + kind : '');
    statusEl.hidden = false;
  }

  const urls = {
    model: wrap.dataset.modelUrl,
    baseColor: wrap.dataset.basecolorUrl,
    metalRough: wrap.dataset.metalroughUrl,
    grainRoughness: wrap.dataset.grainRoughnessUrl,
    grainNormal: wrap.dataset.grainNormalUrl,
  };

  // Ángulo de partida elegido por el mercader en el editor de temas (no
  // hay control público para esto — lo fija quien edita la sección).
  // Grados de desplazamiento sobre el ángulo frontal base; el rango de
  // arrastre del cliente (SWING_HALF_RANGE_DEG) se recentra sobre este
  // mismo ángulo, así que el cliente sigue pudiendo girar ± ese margen
  // alrededor de lo que el mercader eligió como vista por defecto.
  const initialAngleDeg = Number(wrap.dataset.initialAngle) || 0;

  // Multiplicador de distancia de cámara (control del editor, 100% =
  // distancia base). <100% acerca, >100% aleja.
  const cameraDistanceMultiplier = (Number(wrap.dataset.cameraDistance) || 100) / 100;

  try {
    const {
      createRenderer,
      createEnvironment,
      addStudioLights,
      loadGuardianTextures,
      applyGuardianMaterials,
      loadGuardianModel,
      centerAndScaleModel,
    } = await import(`./guardian-viewer-core.js?v=${GUARDIAN_CORE_VERSION}`);
    const THREE = await import('three');
    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
    const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
    const { RoomEnvironment } = await import('three/addons/environments/RoomEnvironment.js');

    const renderer = createRenderer(THREE, wrap, { alpha: true });
    const scene = new THREE.Scene();
    scene.environment = await createEnvironment(THREE, renderer, RoomEnvironment);

    const camera = new THREE.PerspectiveCamera(35, wrap.clientWidth / wrap.clientHeight, 0.01, 100);

    // Vista de frente real: la cara con el logotipo grabado ("Cub.
    // Superior") es la cara ancha y plana del modelo, orientada a lo
    // largo del eje Y local (el grosor de la cartera es la dimensión Y,
    // muy fina) — así que mirar derecho a lo largo del eje Y es mirar de
    // frente a esa cara, sin ningún ángulo/escorzo.
    //
    // Encuadre en vertical: la cara frontal es más ANCHA que alta (el
    // lado largo de la cartera, eje X local, es mayor que el otro lado,
    // eje Z) — vista "tal cual" saldría en horizontal, pero el hueco del
    // hero es alto y estrecho. En vez de rotar el propio modelo (que
    // volvería a descentrarlo — ver nota en centerAndScaleModel), giramos
    // 90° el vector "up" de la cámara: así la dimensión ancha del modelo
    // (eje X) pasa a leerse en vertical en pantalla, y el encuadre encaja
    // en el hueco vertical sin tocar la pieza ni su centrado.
    camera.up.set(1, 0, 0);

    // Distancia base de la cámara al frente de la pieza (tras centrar el
    // modelo a tamaño 0.95 — ver centerAndScaleModel), multiplicada por
    // el control de zoom del editor (camera_distance, 100% = esta base).
    const BASE_DISTANCE = 1.5;
    const baseDistance = BASE_DISTANCE * cameraDistanceMultiplier;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    // Sin controls.autoRotate: ese giro de OrbitControls es monótono (solo
    // suma al ángulo), así que con el rango de seguridad acotado
    // (SAFE_SWING_MIN/MAX_DEG) acabaría encasquillado en el límite, de
    // perfil completo — el peor ángulo para lucir la pieza. En su lugar,
    // más abajo (ver SWAY_*) implementamos un balanceo propio: oscila unos
    // pocos grados a cada lado del ángulo de reposo actual con una onda
    // seno, así que nunca se sale del rango seguro ni se queda parado en
    // un extremo.
    controls.autoRotate = false;
    controls.minDistance = 0.3;
    controls.maxDistance = 4;
    controls.enableZoom = false;

    // Límite DURO de rotación, medido a mano: más allá de estos grados (a
    // cada lado del frente) empieza a verse el hueco interior de las
    // tarjetas desde un ángulo muy rasante.
    const SAFE_SWING_MIN_DEG = -80;
    const SAFE_SWING_MAX_DEG = 80;

    // OrbitControls calcula su ángulo azimutal internamente en un
    // sistema auxiliar "Y-up" — rota lo que sea que tengas en
    // `camera.up` hasta que coincida con (0,1,0) mediante un cuaternión,
    // y SOLO EN ESE sistema tiene sentido su spherical.theta (por eso
    // antes, con up=(0,1,0) por defecto, ese cuaternión era la
    // identidad y se podía usar THREE.Spherical directamente sobre el
    // offset en espacio mundo). Ahora que `camera.up` es (1,0,0),
    // reproducimos el mismo cuaternión a mano para que
    // min/maxAzimuthAngle sigan midiendo lo mismo que ve OrbitControls.
    const upQuat = new THREE.Quaternion().setFromUnitVectors(camera.up, new THREE.Vector3(0, 1, 0));
    const upQuatInverse = upQuat.clone().invert();

    const baseOffsetWorld = new THREE.Vector3(0, baseDistance, 0); // vista de frente pura
    const baseSpherical = new THREE.Spherical().setFromVector3(baseOffsetWorld.clone().applyQuaternion(upQuat));

    const clampedAngleDeg = THREE.MathUtils.clamp(initialAngleDeg, SAFE_SWING_MIN_DEG, SAFE_SWING_MAX_DEG);
    const startSpherical = baseSpherical.clone();
    startSpherical.theta += THREE.MathUtils.degToRad(clampedAngleDeg);
    const offsetWorld = new THREE.Vector3().setFromSpherical(startSpherical).applyQuaternion(upQuatInverse);
    camera.position.copy(controls.target).add(offsetWorld);

    controls.minAzimuthAngle = baseSpherical.theta + THREE.MathUtils.degToRad(SAFE_SWING_MIN_DEG);
    controls.maxAzimuthAngle = baseSpherical.theta + THREE.MathUtils.degToRad(SAFE_SWING_MAX_DEG);

    addStudioLights(THREE, scene);

    // --- Balanceo suave en reposo (sway) --------------------------------
    // Oscilación lenta en seno alrededor del ángulo donde esté la cámara
    // en cada momento (el inicial, o el que haya dejado el cliente tras
    // soltar el arrastre). Se calcula a mano con la MISMA conversión
    // up-vector (upQuat/upQuatInverse) que ya usa el ángulo inicial unas
    // líneas arriba, así que respeta igual el encuadre "de frente" y el
    // rango SAFE_SWING_MIN/MAX_DEG.
    //
    // Dos componentes, cada una con su propio período y una fase
    // desplazada entre sí (PITCH_PHASE), para que la trayectoria combinada
    // no sea una simple diagonal recta sino algo más orgánico:
    //   - theta (giro horizontal, izq/dcha — "negar con la cabeza")
    //   - phi   (cabeceo vertical, arriba/abajo — "asentir")
    const SWAY_AMPLITUDE_DEG = 30; // grados a cada lado del ángulo de reposo
    const SWAY_PERIOD_MS = 20000; // ciclo completo del giro horizontal
    const PITCH_PERIOD_MS = 15000; // ciclo completo del cabeceo vertical (distinto, no sincronizado)
    const PITCH_PHASE_RAD = Math.PI / 2; // desfase del cabeceo respecto al giro
    const SWAY_RESUME_DELAY_MS = 1200; // espera tras soltar el arrastre

    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let swayAllowed = !reduceMotionQuery.matches;
    reduceMotionQuery.addEventListener('change', (e) => {
      swayAllowed = !e.matches;
    });

    // Ángulo de reposo actual, como desviación en radianes sobre
    // baseSpherical.theta/.phi (igual que clampedAngleDeg al arrancar; no
    // hay control de cabeceo inicial, así que restPhiOffsetRad arranca en 0).
    let restThetaOffsetRad = THREE.MathUtils.degToRad(clampedAngleDeg);
    let restPhiOffsetRad = 0;
    let userInteracting = false;
    let idleSinceMs = performance.now();

    controls.addEventListener('start', () => {
      userInteracting = true;
    });
    controls.addEventListener('end', () => {
      userInteracting = false;
      idleSinceMs = performance.now();
      // Retoma el balanceo desde el ángulo donde el cliente soltó el
      // arrastre, no desde el ángulo inicial de la ficha.
      const offset = camera.position.clone().sub(controls.target).applyQuaternion(upQuat);
      const restSpherical = new THREE.Spherical().setFromVector3(offset);
      restThetaOffsetRad = restSpherical.theta - baseSpherical.theta;
      restPhiOffsetRad = restSpherical.phi - baseSpherical.phi;
    });

    function onResize() {
      const w = wrap.clientWidth, h = wrap.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener('resize', onResize);

    function animate() {
      requestAnimationFrame(animate);

      if (swayAllowed && !userInteracting && performance.now() - idleSinceMs > SWAY_RESUME_DELAY_MS) {
        const now = performance.now();
        const swayThetaRad =
          THREE.MathUtils.degToRad(SWAY_AMPLITUDE_DEG) * Math.sin((now / SWAY_PERIOD_MS) * Math.PI * 2);
        const swayPhiRad =
          THREE.MathUtils.degToRad(SWAY_AMPLITUDE_DEG) *
          Math.sin((now / PITCH_PERIOD_MS) * Math.PI * 2 + PITCH_PHASE_RAD);
        const swaySpherical = new THREE.Spherical(
          baseSpherical.radius,
          baseSpherical.phi + restPhiOffsetRad + swayPhiRad,
          baseSpherical.theta + restThetaOffsetRad + swayThetaRad
        );
        const swayOffset = new THREE.Vector3().setFromSpherical(swaySpherical).applyQuaternion(upQuatInverse);
        camera.position.copy(controls.target).add(swayOffset);
      }

      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    const textures = loadGuardianTextures(THREE, urls, renderer);

    // Modelo de partida: si hay bloques de color configurados, se carga
    // el .glb del que esté marcado como activo en el editor (normalmente
    // el primero), en vez del `guardian-model.glb` genérico del propio
    // div del visor — así la página nunca abre con un color "por
    // defecto" que no corresponde a ninguna muestra real.
    const swatchButtonsEl = Array.from(root.querySelectorAll('[data-guardian-color-swatches] .guardian-swatch'));
    const initialSwatch = swatchButtonsEl.find((b) => b.classList.contains('is-active')) || swatchButtonsEl[0];
    const initialModelUrl = (initialSwatch && initialSwatch.dataset.modelUrl) || urls.model;

    setStatus('Cargando modelo…');
    let currentModel = await loadGuardianModel(GLTFLoader, initialModelUrl);
    applyGuardianMaterials(currentModel, textures);
    // Nota: NO se aplica ninguna rotación estática al modelo tras
    // centrarlo. centerAndScaleModel calcula el centro de la caja
    // envolvente con el modelo en su rotación original (identidad) y
    // desplaza `position` para que ese centro caiga en el origen — girar
    // el modelo (`model.rotation.*`) después de eso gira alrededor del
    // origen LOCAL del archivo 3D (que no tiene por qué coincidir con el
    // centro visual), desplazando la pieza fuera de ese centro y dando
    // sensación de que "se escora". El giro que ve el usuario lo aporta
    // solo la cámara (OrbitControls, orbitando alrededor de
    // controls.target = origen = centro real de la pieza), así que el eje
    // de rotación queda siempre centrado y la cartera no se inclina.
    centerAndScaleModel(THREE, currentModel, 0.95);
    scene.add(currentModel);
    if (statusEl) statusEl.hidden = true;

    // --- Selector de color (bloques "color_variant" del editor) -------
    // Cada muestra apunta a un .glb distinto ya exportado con ese color.
    // Al pulsar una, cargamos el nuevo modelo reutilizando la MISMA
    // cámara, luces y texturas (solo cambian la geometría/material del
    // propio archivo) y liberamos la memoria del modelo anterior.
    const swatchButtons = swatchButtonsEl;

    // Botón "Comprar ahora": si el color seleccionado tiene su propia
    // página de producto (campo "product_url" del bloque), el botón
    // apunta ahí; si ese color no tiene enlace propio, se vuelve a caer
    // en el enlace por defecto del botón (data-default-href).
    const buyLink = root.querySelector('[data-guardian-buy-link]');
    function updateBuyLink(btn) {
      if (!buyLink) return;
      const productUrl = btn?.dataset.productUrl;
      buyLink.setAttribute('href', productUrl && productUrl.trim() ? productUrl : buyLink.dataset.defaultHref || '#');
    }

    if (swatchButtons.length) {
      let swapping = false;

      // Estado inicial: el swatch marcado como activo en el editor
      // (normalmente el primero) manda su enlace al botón desde el
      // primer render, antes de que el cliente toque nada.
      const initialActive = swatchButtons.find((b) => b.classList.contains('is-active')) || swatchButtons[0];
      updateBuyLink(initialActive);

      function disposeModel(obj) {
        obj.traverse((child) => {
          if (!child.isMesh) return;
          child.geometry?.dispose();
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((m) => m && m.dispose());
        });
      }

      swatchButtons.forEach((btn) => {
        btn.addEventListener('click', async () => {
          const newUrl = btn.dataset.modelUrl;
          if (!newUrl || swapping || btn.classList.contains('is-active')) return;
          swapping = true;
          swatchButtons.forEach((b) => (b.disabled = true));
          setStatus('Cambiando de color…');
          try {
            const newModel = await loadGuardianModel(GLTFLoader, newUrl);
            applyGuardianMaterials(newModel, textures);
            centerAndScaleModel(THREE, newModel, 0.95);
            scene.remove(currentModel);
            disposeModel(currentModel);
            scene.add(newModel);
            currentModel = newModel;
            swatchButtons.forEach((b) => {
              b.classList.toggle('is-active', b === btn);
              b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
            });
            updateBuyLink(btn);
            if (statusEl) statusEl.hidden = true;
          } catch (err) {
            console.error('[guardian-hero] color swap', err);
            setStatus('No se pudo cargar ese color.', 'error');
          } finally {
            swapping = false;
            swatchButtons.forEach((b) => (b.disabled = false));
          }
        });
      });
    }
  } catch (err) {
    console.error('[guardian-hero]', err);
    setStatus('Error al cargar el visor 3D.', 'error');
  }
}

document.querySelectorAll('[data-guardian-hero]').forEach(initGuardianHero);
